import React, { useEffect, useRef, useState } from 'react';
import { api, AuthUser, CompanyRole } from './services/api';
import { LoginPage } from './pages/LoginPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { SettingsPage } from './pages/SettingsPage';
import { LangProvider, LangSelect, useCabinetLang } from './i18n';
import './styles/cashalot.css';

type Page = 'documents' | 'settings';

function surnameInitial(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const last = parts.length > 1 ? parts[parts.length - 1] : parts[0];
  return (last[0] || '?').toUpperCase();
}

const AppShell: React.FC = () => {
  const { t } = useCabinetLang();
  const [user, setUser] = useState<AuthUser | null>(api.getStoredUser());
  const [page, setPage] = useState<Page>('documents');
  const [docsMenuOpen, setDocsMenuOpen] = useState(true);
  const [companyRoles, setCompanyRoles] = useState<CompanyRole[]>(() => api.getCompanyRoles());
  const [selectedCompany, setSelectedCompany] = useState<string>(() => api.getSelectedCompanyId() || '');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!profileMenuRef.current?.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [profileMenuOpen]);

  const handleLogout = () => {
    api.clearSession();
    setUser(null);
    setCompanyRoles([]);
    setSelectedCompany('');
  };

  const selectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    api.setSelectedCompanyId(companyId || null);
  };

  if (!user) {
    return (
      <LoginPage
        onSuccess={() => {
          setUser(api.getStoredUser());
          setCompanyRoles(api.getCompanyRoles());
          setSelectedCompany(api.getSelectedCompanyId() || '');
        }}
      />
    );
  }

  const companyOptions = companyRoles.filter((r) => r.company_id);
  const selectedName =
    companyOptions.find((r) => r.company_id === selectedCompany)?.company_name ||
    companyOptions[0]?.company_name ||
    t.company;

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-top">
          <div className="logo-block">
            <div className="logo">
              <span className="logo-text">Cashalot</span>
              <span className="logo-edo">ЭДО</span>
            </div>
            <div className="logo-subtitle">{t.edoSubtitle}</div>
          </div>
          <div className="header-actions">
            <select
              className="header-control park-select"
              value={selectedCompany || companyOptions[0]?.company_id || ''}
              onChange={(e) => selectCompany(e.target.value)}
              aria-label={t.company}
            >
              {companyOptions.length === 0 && <option value="">{selectedName}</option>}
              {companyOptions.map((cr) => (
                <option key={cr.company_id!} value={cr.company_id!}>
                  {cr.company_name || cr.company_id}
                </option>
              ))}
            </select>

            <div className="user-chip profile-menu" ref={profileMenuRef}>
              <span className="user-name" title={user.name}>
                {user.name}
              </span>
              <button
                type="button"
                className="user-avatar-btn"
                title={t.profileMenu}
                aria-label={t.profileMenu}
                onClick={() => setProfileMenuOpen((v) => !v)}
              >
                {surnameInitial(user.name)}
              </button>
              {profileMenuOpen && (
                <div className="profile-dropdown">
                  <button
                    type="button"
                    className="profile-dropdown-item"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setPage('settings');
                    }}
                  >
                    {t.settings}
                  </button>
                  <button
                    type="button"
                    className="profile-dropdown-item"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleLogout();
                    }}
                  >
                    {t.logout}
                  </button>
                </div>
              )}
            </div>

            <LangSelect className="lang-switch" />
          </div>
        </div>
      </header>

      <div className="app-body">
        <aside className="sidebar">
          <nav className="menu">
            <div className="menu-section">
              <button
                type="button"
                className={`menu-section-toggle ${page === 'documents' ? 'active' : ''}`}
                onClick={() => {
                  setDocsMenuOpen((open) => (page === 'documents' ? !open : true));
                  setPage('documents');
                }}
              >
                <span>{t.documents}</span>
                <span className={`toggle-icon ${docsMenuOpen ? 'open' : ''}`}>▼</span>
              </button>
              {docsMenuOpen && (
                <button
                  type="button"
                  className={`menu-item ${page === 'documents' ? 'active' : ''}`}
                  onClick={() => setPage('documents')}
                >
                  {t.avrTitle}
                </button>
              )}
            </div>
            <div className="menu-bottom">
              <button
                type="button"
                className={`menu-item ${page === 'settings' ? 'active' : ''}`}
                onClick={() => setPage('settings')}
              >
                {t.settings}
              </button>
            </div>
          </nav>
        </aside>

        <main className="main-content">
          {page === 'documents' && <DocumentsPage title={t.avrTitle} />}
          {page === 'settings' && <SettingsPage />}
        </main>
      </div>
    </div>
  );
};

export const App: React.FC = () => (
  <LangProvider>
    <AppShell />
  </LangProvider>
);
